import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { orderBy } from 'natural-orderby';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import { getFormattedTimeFromMilliseconds } from '../../utils/dataFormatUtils';
import GaugeTicket from './GaugeTicket';
/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics }) {
    if (metricRequests.length === 0 || metricRequests[0].metrics.length === 0) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource], true)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

export default function GaugeTicketWrapper(props) {
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);
    const wrapperRef = useRef();
    const [activeIndex, setActiveIndex] = useState();
    const [titleWidth, setTitleWidth] = useState(0);

    useEffect(() => {
        if (wrapperRef?.current) {
            if (props.configuration.titleWidth) {
                setTitleWidth(props.configuration.titleWidth);
            } else {
                const elements = Array.from(
                    wrapperRef.current.querySelectorAll(
                        '.selector-container__title'
                    )
                );
                const maxWidth = Math.max(
                    ...elements.map((el) => el.clientWidth)
                );
                setTitleWidth(maxWidth);
            }
        }
    }, [props.configuration.titleWidth]);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const formatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const sortObject = props.configuration.sortObject ?? {
        columnName: metricRequest.groupBy[0],
        sortOrder: 'asc',
    };

    const metricData = orderBy(
        metricSource.metricSeries,
        sortObject.columnName,
        sortObject.sortOrder
    );

    return (
        <>
            {metricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container" ref={wrapperRef}>
                {metricData.map((data, index) => {
                    const dataLabel = data[metricRequest.groupBy[0]];
                    const title =
                        data[props.configuration.displayGroupKey] ?? dataLabel;

                    return (
                        <GaugeTicket
                            key={dataLabel}
                            isActive={activeIndex === index}
                            isSelectable={props.isSelectable}
                            metricRequest={metricRequest}
                            onSelectorClick={() => {
                                props.onSelectorClick(data);
                                setActiveIndex(index);
                            }}
                            onSelectorRemoveClick={() => {
                                props.onSelectorRemoveClick();
                                setActiveIndex(undefined);
                            }}
                            openLinksInNewTab={props.openLinksInNewTab}
                            thresholds={props.thresholds}
                            dataLabel={dataLabel}
                            title={title}
                            titleWidth={titleWidth}
                            values={data}
                            formatter={formatter}
                            configuration={props.configuration}
                            columns={props.configuration.columns}
                        />
                    );
                })}
            </div>
        </>
    );
}
/* eslint-enable complexity */

GaugeTicketWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    isSelectable: PropTypes.bool.isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    onSelectorRemoveClick: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
    onSelectorClick: PropTypes.func,
};
